import React, { useCallback, useEffect, useRef, useState } from "react";
import ExcelJS from "exceljs";
import { useDispatch, useSelector } from "react-redux";
import { addItems, categoryData } from "../../../redux/slices/categorySlice";
import { adminApiService } from "../../../service/admin/adminApiService";
import { categoryApiService } from "../../../service/admin/categoryApiService";
import { toast } from "react-toastify";

export default function SkuUpload() {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [jobs, setJobs] = useState([]);

  // Category browse
  const [catLoading, setCatLoading] = useState(false);
  const [catError, setCatError] = useState("");
  const [rootTree, setRootTree] = useState([]);
  const [showCategoryBox, setShowCategoryBox] = useState(false);
  const [browseCurrent, setBrowseCurrent] = useState([]);
  const [browseStack, setBrowseStack] = useState([]);
  const [browsePath, setBrowsePath] = useState([]);
  const [infoMsg, setInfoMsg] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);

  const [categoryCsvFile, setCategoryCsvFile] = useState(null);
  const categoryCsvInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const dispatch = useDispatch();
  const categoriesFromStore = useSelector(categoryData);
  const pollingRef = useRef(null);
  const prevJobsRef = useRef([]);

  /** Failed SKU modal: loaded from GET …/progress/:jobId */
  const [failModal, setFailModal] = useState(null);
  const [failModalLoading, setFailModalLoading] = useState(false);

  // ---------- Category loading ----------
  useEffect(() => {
    if (Array.isArray(categoriesFromStore) && categoriesFromStore.length) {
      setRootTree(normalizeCategoryArray(categoriesFromStore));
      setCatError(""); setCatLoading(false);
    }
  }, [categoriesFromStore]);

  useEffect(() => {
    if (categoriesFromStore && Array.isArray(categoriesFromStore) && categoriesFromStore.length) return;
    let mounted = true;
    (async () => {
      try {
        setCatLoading(true);
        setCatError("");
        // const res = await categoryApiService.getExistingCategory();
        const res = await categoryApiService.getAllCategoriesForSkuUpload();
        if (!mounted) return;
        if (res?.status?.code === 200 && Array.isArray(res.data)) {
          dispatch(addItems(res.data));
          setRootTree(normalizeCategoryArray(res.data));
        } else { setCatError(res?.error?.message || "Failed to load categories"); }
      } catch { if (mounted) setCatError("Server error. Try again later"); }
      finally { if (mounted) setCatLoading(false); }
    })();
    return () => { mounted = false; };
  }, [categoriesFromStore, dispatch]);

  // ---------- Fetch jobs ----------
  const fetchJobs = useCallback(async () => {
    try {
      const res = await adminApiService.getSkuUploadJobs();
      if (res?.status?.code === 200 && res?.data?.jobs) {
        const next = res.data.jobs;
        const prev = prevJobsRef.current;

        // Toast for newly completed/failed
        for (const nj of next) {
          const pj = prev.find((p) => p.jobId === nj.jobId);
          if (pj?.status === "running" && nj.status === "completed") {
            toast.success(`Job done — ${nj.success} success, ${nj.failed} failed of ${nj.totalSkus}`, { autoClose: 10000 });
          } else if (pj?.status === "running" && (nj.status === "failed" || nj.status === "cancelled")) {
            toast.error(`Job ${nj.status}`, { autoClose: 10000 });
          }
        }

        prevJobsRef.current = next;
        setJobs(next);
      }
    } catch (_) {}
  }, []);

  // Fetch on mount
  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  // Poll every 3s while any job is running
  useEffect(() => {
    const hasRunning = jobs.some((j) => j.status === "running");
    if (!hasRunning) {
      if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
      return;
    }
    if (pollingRef.current) clearInterval(pollingRef.current);
    pollingRef.current = setInterval(fetchJobs, 3000);
    return () => { if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; } };
  }, [jobs, fetchJobs]);

  // ---------- Category helpers ----------
  const normalizeCategoryArray = (arr, parentPath = "") =>
    (arr || []).map((node) => {
      const name = node?.c_name ?? node?.name ?? "";
      const nodeID = node?.nodeID ?? node?.nodeId ?? node?.node_id ?? node?.id ?? node?._id ?? null;
      const fullPath = parentPath ? `${parentPath} > ${name}` : name;
      return { name, nodeID, fullPath, children: normalizeCategoryArray(node?.children || [], fullPath) };
    });

  const pickNodeShape = (node) => ({
    name: node?.name, nodeID: node?.nodeID, fullPath: node?.fullPath, children: node?.children || [],
  });

  const toggleBrowse = () => {
    if (showCategoryBox) { setShowCategoryBox(false); return; }
    setBrowseCurrent(rootTree); setBrowseStack([]); setBrowsePath([]); setShowCategoryBox(true); setInfoMsg("");
  };

  const onEnterNode = (node) => {
    const { children, name } = pickNodeShape(node);
    setBrowseStack((s) => [...s, browseCurrent]);
    setBrowseCurrent(children || []);
    setBrowsePath((p) => [...p, name]); setInfoMsg("");
  };

  const onBack = () => {
    if (!browseStack.length) { setShowCategoryBox(false); return; }
    const s = [...browseStack]; const prev = s.pop();
    setBrowseStack(s); setBrowseCurrent(prev || []); setBrowsePath((p) => p.slice(0, -1)); setInfoMsg("");
  };

  const onSelectLeaf = (node) => {
    const { fullPath, nodeID, children } = pickNodeShape(node);
    if (children && children.length > 0) { setInfoMsg("Please drill down to a sub-category (leaf) and then select."); return; }
    if (!nodeID) { setInfoMsg("Node ID missing for this category (cannot select)."); return; }
    if (selectedCategories.some((c) => c.nodeID === String(nodeID))) { setInfoMsg("Already selected"); return; }
    setSelectedCategories((prev) => [...prev, { nodeID: String(nodeID), path: fullPath }]);
    setShowCategoryBox(false); setInfoMsg("");
  };

  const removeSelected = (id) => setSelectedCategories((prev) => prev.filter((c) => c.nodeID !== String(id)));

  // ---------- Download Excel ----------
  const downloadCategoriesExcel = async () => {
    if (selectedCategories.length === 0) return;
    const wb = new ExcelJS.Workbook();
    const ws1 = wb.addWorksheet("Selected Categories");
    ws1.columns = [{ header: "Node Id", key: "nodeId" }, { header: "Node Path", key: "nodePath" }];
    ws1.getRow(1).font = { bold: true };
    ws1.addRows(selectedCategories.map((c) => ({ nodeId: c.nodeID, nodePath: c.path })));
    ws1.getColumn("nodeId").width = Math.max(10, ...selectedCategories.map((r) => String(r.nodeID).length + 2));
    ws1.getColumn("nodePath").width = Math.min(120, Math.max(40, Math.ceil(Math.max(...selectedCategories.map((r) => r.path.length)) * 1.2)));
    ws1.getColumn("nodeId").numFmt = "@"; ws1.getColumn("nodePath").numFmt = "@";

    const ws2 = wb.addWorksheet("SKUs Data");
    const headerRowValues = selectedCategories.map((c) => String(c.nodeID));
    const r1 = ws2.addRow(headerRowValues);
    r1.eachCell((cell) => {
      cell.font = { bold: true }; cell.alignment = { horizontal: "center", vertical: "middle" };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFEFF6FF" } };
      cell.border = { top: { style: "thin", color: { argb: "FFCDD2D6" } }, left: { style: "thin", color: { argb: "FFCDD2D6" } }, bottom: { style: "thin", color: { argb: "FFCDD2D6" } }, right: { style: "thin", color: { argb: "FFCDD2D6" } } };
    });
    headerRowValues.forEach((id, idx) => {
      const col = ws2.getColumn(idx + 1);
      col.width = Math.max(16, Math.ceil((String(id).length + 2) * 1.25));
      col.numFmt = "@"; col.alignment = { horizontal: "left" };
    });

    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "selected_categories.xlsx";
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  // ---------- Upload ----------
  const handleFileChange = (e) => {
    const f = e.target.files?.[0] || null;
    setCategoryCsvFile(f);
    if (f) setInfoMsg(`File chosen: ${f.name}`); else setInfoMsg("");
  };

  const uploadFile = async () => {
    if (!categoryCsvFile) { setInfoMsg("Please choose a file first."); return; }
    try {
      setUploading(true); setError("");
      const formData = new FormData();
      formData.append("file", categoryCsvFile);
      const res = await adminApiService.uploadSkuList(formData);
      if (res?.status?.code === 200 && res?.data?.jobId) {
        // Add new job to list immediately
        const newJob = {
          jobId: res.data.jobId,
          batchId: res.data.batchId ?? null,
          status: "running", totalSkus: res.data.total,
          processed: 0, success: 0, failed: 0, categories: [], errors: [],
          startedAt: new Date().toISOString(),
        };
        setJobs((prev) => [newJob, ...prev]);
        prevJobsRef.current = [newJob, ...prevJobsRef.current];
        setCategoryCsvFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
        toast.success(`Upload started — ${res.data.total} SKUs queued`);
      } else {
        setError(res?.status?.message || "Upload failed");
      }
    } catch (err) {
      setError(err.message || "An error occurred during upload");
    } finally {
      setUploading(false);
    }
  };

  const cancelJob = async (jobId) => {
    try {
      const res = await adminApiService.cancelSkuJob(jobId);
      if (res?.status?.code === 200) {
        toast.info("Cancel requested");
        setJobs((prev) => prev.map((j) => j.jobId === jobId ? { ...j, cancelRequested: true } : j));
      }
    } catch (_) { toast.error("Failed to cancel"); }
  };

  /** Excel opens long numeric SKUs as text (avoids 1.23E+11). */
  const csvSkuForExcel = (offerId) => {
    const inner = String(offerId ?? "").replace(/"/g, '""');
    return `="${inner}"`;
  };

  const exportFailedSkusCsv = (errors, jobId) => {
    const list = Array.isArray(errors) ? errors : [];
    const skuLines = list.map((e) => csvSkuForExcel(e.offerId));
    const csv = ["SKU", ...skuLines].join("\r\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `failed-skus-${jobId || "export"}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const openFailedSkusModal = async (job) => {
    if (!job?.jobId || !(job.failed > 0)) return;
    setFailModalLoading(true);
    setFailModal({ jobId: job.jobId, errors: null, summary: job });
    try {
      const res = await adminApiService.getSkuUploadProgress(job.jobId);
      const d = res?.data ?? res;
      const errors = Array.isArray(d?.errors) ? d.errors : [];
      setFailModal({
        jobId: job.jobId,
        errors,
        summary: {
          status: d?.status,
          totalSkus: d?.totalSkus,
          failed: d?.failed,
          success: d?.success,
          duration: d?.duration,
          categoryCount: Array.isArray(d?.categories) ? d.categories.length : 0,
        },
      });
    } catch (_) {
      toast.error("Could not load failed SKUs");
      setFailModal(null);
    } finally {
      setFailModalLoading(false);
    }
  };

  // ---------- Helpers ----------
  const formatDuration = (sec) => {
    if (sec == null || Number.isNaN(Number(sec))) return "-";
    const s = Number(sec);
    if (s === 0) return "0s";
    if (s < 60) return `${Math.round(s)}s`;
    return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
  };
  const formatTime = (iso) => {
    if (!iso) return "-";
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const categoryCountFromSummary = (summary) => {
    if (!summary) return undefined;
    if (summary.categoryCount != null) return Number(summary.categoryCount);
    if (Array.isArray(summary.categories)) return summary.categories.length;
    return undefined;
  };

  const runningJobs = jobs.filter((j) => j.status === "running");
  const finishedJobs = jobs.filter((j) => j.status !== "running");

  const viewFailedSkusBtnClass =
    "inline-flex items-center justify-center rounded-lg border border-sky-200/80 bg-[#E5F6FF] px-2.5 h-7 text-xs font-semibold text-slate-800 shadow-sm hover:bg-[#d4efff] focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-40 disabled:hover:bg-[#E5F6FF]";

  return (
    <div className="-m-[14px] w-full max-w-full px-2.5 sm:px-3">
      <div className="w-full">
        <div className="rounded-xl border border-[#e5e7eb] shadow-[0_6px_20px_rgba(0,0,0,0.07)] bg-white dark:bg-[#0f172a] dark:border-[#2f3a46]">
          <div className="p-4 sm:p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              {/* Left column */}
              <div>
                {/* Selected Categories */}
                {selectedCategories.length > 0 && (
                  <div className="mb-2 rounded-xl border border-[#e5e7eb] bg-white dark:bg-[#0f172a] dark:border-[#2f3a46] shadow-[0_6px_20px_rgba(0,0,0,0.07)] overflow-hidden">
                    <div className="flex items-center gap-2 px-3 py-2 bg-gradient-to-b from-white to-[#f9fafb] border-b border-[#eef2f7]">
                      <div className="font-semibold text-[#111827] text-[0.95rem]">Selected categories</div>
                      <div className="ml-auto text-[8.5px] text-[#6b7280] bg-[#eef2ff] border border-[#c7d2fe] px-[6px] py-[1px] rounded-full">{selectedCategories.length}</div>
                    </div>
                    <div className="flex gap-1.5 px-3 py-2 overflow-x-auto">
                      {selectedCategories.map((c) => (
                        <span key={c.nodeID} title={c.path} className="inline-flex items-center gap-1.5 px-1.5 py-1 bg-[#eef9f1] border border-[#22c55e] text-[#065f46] rounded-md text-[11px]">
                          <span className="max-w-[420px] truncate">{c.path}</span>
                          <button type="button" className="bg-transparent border-0 text-[#065f46] font-bold cursor-pointer px-1" aria-label="Remove" onClick={() => removeSelected(c.nodeID)}>✕</button>
                        </span>
                      ))}
                    </div>
                    <div className="flex gap-2 px-3 pb-2 pt-2 border-t border-[#eef2f7]">
                      <button type="button" onClick={downloadCategoriesExcel} disabled={!selectedCategories.length} className="h-8 px-3 text-[0.9rem] rounded-md bg-[#6E65C7] text-white disabled:opacity-55">Download Category Excel</button>
                    </div>
                  </div>
                )}

                {/* Browse Category */}
                <div className="mb-2 relative">
                  <button className="w-full h-8 px-3 text-[0.9rem] rounded-md border border-[#0d6efd] text-[#0d6efd] bg-transparent hover:bg-[rgba(56,189,248,0.08)] disabled:opacity-55" onClick={toggleBrowse} disabled={catLoading || !!catError}>
                    {catLoading ? "Loading categories..." : "Browse Category"}
                  </button>
                  {showCategoryBox && (
                    <div className="absolute left-0 right-0 top-[calc(100%+6px)] z-[1000] rounded-xl border border-[#e5e7eb] shadow-[0_10px_30px_rgba(0,0,0,0.12)] bg-white dark:bg-[#0f172a]">
                      <div className="flex items-center gap-2 px-2.5 py-2 border-b border-[#f1f3f5] bg-gradient-to-b from-white to-[#f9fafb]">
                        <button type="button" onClick={onBack} className="h-7 px-2 text-[12px] rounded-md border border-[#e5e7eb] bg-white cursor-pointer">{browseStack.length ? "←" : "✕"}</button>
                        <div className="font-bold text-[#111827] text-[0.95rem]">Browse Categories</div>
                        <div className="ml-auto text-[12px] text-[#6b7280] truncate max-w-1/2 text-right">{browsePath.length ? browsePath.join(" > ") : "All Categories"}</div>
                      </div>
                      {catError ? (
                        <div className="px-3 py-3 text-center text-[#6b7280]">{catError}</div>
                      ) : browseCurrent.length === 0 ? (
                        <div className="px-3 py-3 text-center text-[#6b7280]">No category data</div>
                      ) : (
                        <div className="max-h-[300px] overflow-y-auto">
                          {browseCurrent.map((node) => {
                            const { name, children } = pickNodeShape(node);
                            const isLeaf = !children || children.length === 0;
                            return (
                              <div key={node.fullPath} className="flex items-center gap-2 px-2.5 py-2 border-b border-[rgba(0,0,0,0.05)] hover:bg-[rgba(0,123,255,0.05)]">
                                <div className="flex-1 min-w-0 truncate">{name}</div>
                                {isLeaf ? (
                                  <button type="button" onClick={() => onSelectLeaf(node)} className="h-7 px-2 text-[12px] rounded-md bg-[#10b981] text-white hover:bg-[#059669] cursor-pointer">Select</button>
                                ) : (
                                  <button type="button" onClick={() => onEnterNode(node)} className="h-7 px-2 text-[12px] rounded-md border border-[#e5e7eb] bg-white hover:bg-[#f9fafb] cursor-pointer">View →</button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {infoMsg && <div className="px-2.5 py-2 border-t border-[#f1f3f5] text-[12px] text-[#b45309] bg-[#fffbeb]">{infoMsg}</div>}
                    </div>
                  )}
                </div>

                {/* Upload row */}
                <div className="flex items-center gap-2 py-2">
                  <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" disabled={uploading} onChange={handleFileChange} className="max-w-[260px] h-[34px] text-[0.92rem] px-2 py-1 border border-[#e5e7eb] rounded-md" />
                  <input ref={categoryCsvInputRef} type="file" accept=".csv,.xlsx,.xls" onChange={handleFileChange} className="hidden" />
                  <button type="button" disabled={uploading || !categoryCsvFile} onClick={uploadFile}
                    className="h-8 px-3 text-[0.9rem] rounded-md border border-[#6c757d] text-[#6c757d] bg-transparent hover:bg-[rgba(0,0,0,0.03)] disabled:opacity-55">
                    {uploading ? (<span className="inline-flex items-center gap-2"><span className="w-4 h-4 rounded-full border-2 border-[#6c757d] border-t-transparent animate-spin" />Uploading...</span>) : "Upload SKUs Category"}
                  </button>
                </div>
              </div>

              {/* Right column */}
              <div>
                <div className="rounded-md bg-[#e5f6ff] text-[#084c61] px-3 py-3">
                  <h6 className="m-0 mb-1 font-semibold">Instructions:</h6>
                  <ul className="m-0 pl-4 text-sm list-disc">
                    <li>Click <strong>Download Category Excel</strong> to get .xlsx</li>
                    <li>Sheet1: Node Id + Node Path</li>
                    <li>Sheet2: NodeIDs as header</li>
                    <li>Each upload creates a separate job — upload multiple files anytime</li>
                  </ul>
                </div>
              </div>
            </div>

            {error && <div className="rounded-md bg-[#fee2e2] text-[#991b1b] px-3 py-3 mb-3"><strong>Error:</strong> {error}</div>}

            {/* ========== Active Jobs ========== */}
            {runningJobs.length > 0 && (
              <div className="mb-4">
                <h5 className="mb-2 font-semibold text-[#111827]">Active Jobs ({runningJobs.length})</h5>
                <div className="space-y-3">
                  {runningJobs.map((job) => {
                    const pct = job.totalSkus ? Math.round((job.processed / job.totalSkus) * 100) : 0;
                    return (
                      <div key={job.jobId} className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                            <span className="text-sm font-medium text-blue-800">Processing — {pct}%</span>
                            <span className="text-xs text-blue-600">Started {formatTime(job.startedAt)}</span>
                            {job.batchId && (
                              <span className="text-xs font-mono text-blue-700" title="SKU upload batch">{job.batchId}</span>
                            )}
                            {job.categories?.length > 0 && (
                              <span className="text-xs text-blue-500">({job.categories.length} categories)</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {job.failed > 0 && (
                              <button
                                type="button"
                                onClick={() => openFailedSkusModal(job)}
                                className={viewFailedSkusBtnClass}
                              >
                                View failed SKUs
                              </button>
                            )}
                            <button onClick={() => cancelJob(job.jobId)} disabled={job.cancelRequested}
                              className="text-xs px-2 py-1 rounded border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-50">
                              {job.cancelRequested ? "Cancelling..." : "Cancel"}
                            </button>
                          </div>
                        </div>
                        <div className="w-full bg-blue-200 rounded-full h-2.5 mb-2">
                          <div className="bg-blue-600 h-2.5 rounded-full transition-all duration-300" style={{ width: `${pct}%` }} />
                        </div>
                        <div className="grid grid-cols-4 gap-2 text-center">
                          <div className="rounded bg-[#0d6efd] text-white py-1.5">
                            <div className="text-lg font-bold">{job.totalSkus}</div>
                            <div className="text-[10px] opacity-90">Total SKUs</div>
                          </div>
                          <div className="rounded bg-[#198754] text-white py-1.5">
                            <div className="text-lg font-bold">{job.success}</div>
                            <div className="text-[10px] opacity-90">Mapped</div>
                          </div>
                          <div className="rounded bg-[#dc3545] text-white py-1.5">
                            <div className="text-lg font-bold">{job.failed}</div>
                            <div className="text-[10px] opacity-90">Failed</div>
                          </div>
                          <div className="rounded bg-[#0dcaf0] text-white py-1.5">
                            <div className="text-lg font-bold">{job.processed}/{job.totalSkus}</div>
                            <div className="text-[10px] opacity-90">Processed</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ========== Recent Jobs ========== */}
            {finishedJobs.length > 0 && (
              <div>
                <h5 className="mb-2 font-semibold text-[#111827]">Recent Jobs</h5>
                <div className="overflow-x-auto rounded-lg border border-[#e5e7eb]">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-[#f9fafb] text-[#6b7280] text-xs uppercase">
                      <tr>
                        <th className="px-3 py-2">Batch ID</th>
                        <th className="px-3 py-2">Status</th>
                        <th className="px-3 py-2">Categories</th>
                        <th className="px-3 py-2">Total</th>
                        <th className="px-3 py-2">Success</th>
                        <th className="px-3 py-2">Failed</th>
                        <th className="px-3 py-2">Started</th>
                        <th className="px-3 py-2">Duration</th>
                        <th className="px-3 py-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {finishedJobs.map((job) => (
                        <tr key={job.jobId} className="border-t border-[#e5e7eb] hover:bg-[#f9fafb]">
                          <td className="px-3 py-2 font-mono text-xs text-[#374151] max-w-[11rem] truncate" title={job.batchId || ""}>
                            {job.batchId || "—"}
                          </td>
                          <td className="px-3 py-2">
                            <span className={`inline-block px-2 py-0.5 rounded-full text-white text-xs ${
                              job.status === "completed" ? "bg-green-600" : job.status === "failed" ? "bg-red-600" : "bg-gray-500"
                            }`}>{job.status}</span>
                          </td>
                          <td className="px-3 py-2">{job.categories?.length || 0}</td>
                          <td className="px-3 py-2">{job.totalSkus}</td>
                          <td className="px-3 py-2 text-green-700">{job.success}</td>
                          <td className="px-3 py-2 text-red-600">{job.failed}</td>
                          <td className="px-3 py-2">{formatTime(job.startedAt)}</td>
                          <td className="px-3 py-2">{formatDuration(job.duration)}</td>
                          <td className="px-3 py-2">
                            <button
                              type="button"
                              disabled={!(job.failed > 0)}
                              onClick={() => openFailedSkusModal(job)}
                              className={viewFailedSkusBtnClass}
                            >
                              View failed SKUs
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {jobs.length === 0 && !uploading && (
              <div className="text-center text-[#9ca3af] py-6 text-sm">No jobs yet. Upload an Excel file to start processing SKUs.</div>
            )}
          </div>
        </div>
      </div>

      {failModal && (
        <div
          className="fixed inset-0 z-[2000] flex items-center justify-center p-3 sm:p-4 bg-slate-900/50 backdrop-blur-[1px]"
          role="dialog"
          aria-modal="true"
          aria-labelledby="sku-fail-modal-title"
          onClick={() => !failModalLoading && setFailModal(null)}
        >
          <div
            className="sku-fail-modal flex max-h-[min(80vh,720px)] w-full max-w-3xl flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-2xl ring-1 ring-slate-900/5"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="flex shrink-0 items-start justify-between gap-3 border-b border-slate-700/30 bg-slate-800 px-3 py-2.5 text-white">
              <div className="min-w-0 flex-1">
                <h2 id="sku-fail-modal-title" className="m-0 text-sm font-semibold tracking-tight text-white">
                  Failed SKUs
                </h2>
                <p className="m-0 mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11px] leading-snug text-slate-200">
                  {failModal.summary?.status != null && (
                    <span className="capitalize text-slate-100">{failModal.summary.status}</span>
                  )}
                  {(() => {
                    const n = categoryCountFromSummary(failModal.summary);
                    if (n == null) return null;
                    return (
                      <>
                        <span className="text-slate-500" aria-hidden>
                          ·
                        </span>
                        <span>
                          {n} {n === 1 ? "category" : "categories"}
                        </span>
                      </>
                    );
                  })()}
                  {failModal.summary?.success != null && (
                    <>
                      <span className="text-slate-500" aria-hidden>
                        ·
                      </span>
                      <span>{failModal.summary.success} success</span>
                    </>
                  )}
                  {failModal.summary?.failed != null && (
                    <>
                      <span className="text-slate-500" aria-hidden>
                        ·
                      </span>
                      <span>{failModal.summary.failed} failed</span>
                    </>
                  )}
                  {failModal.summary?.totalSkus != null && (
                    <>
                      <span className="text-slate-500" aria-hidden>
                        ·
                      </span>
                      <span>{failModal.summary.totalSkus} total</span>
                    </>
                  )}
                  {failModal.summary?.duration != null && (
                    <>
                      <span className="text-slate-500" aria-hidden>
                        ·
                      </span>
                      <span>Duration {formatDuration(failModal.summary.duration)}</span>
                    </>
                  )}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2 pt-0.5">
                <button
                  type="button"
                  className="inline-flex h-7 items-center rounded-md bg-white px-2.5 text-xs font-semibold text-slate-900 shadow-sm hover:bg-slate-100 disabled:pointer-events-none disabled:opacity-50"
                  disabled={failModalLoading || !Array.isArray(failModal.errors) || failModal.errors.length === 0}
                  onClick={() => exportFailedSkusCsv(failModal.errors, failModal.jobId)}
                >
                  Export CSV
                </button>
                <button
                  type="button"
                  className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-lg leading-none text-slate-300 transition-colors hover:bg-white/10 hover:text-white disabled:pointer-events-none disabled:opacity-50"
                  aria-label="Close"
                  disabled={failModalLoading}
                  onClick={() => setFailModal(null)}
                >
                  ×
                </button>
              </div>
            </header>
            <div
              className="min-h-0 flex-1 overflow-y-auto scroll-smooth overscroll-contain bg-slate-50/80 px-3 py-2.5 [scrollbar-width:thin] [scrollbar-color:rgb(148_163_184)_rgb(241_245_249)] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-slate-300 hover:[&::-webkit-scrollbar-thumb]:bg-slate-400 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-slate-100"
            >
              {failModalLoading || failModal.errors == null ? (
                <div className="py-8 text-center text-sm text-slate-600">Loading…</div>
              ) : failModal.errors.length === 0 ? (
                <div className="py-6 text-sm text-slate-600">No failure rows returned for this job.</div>
              ) : (
                <div className="overflow-hidden rounded-md border border-slate-200 bg-white">
                  <table className="w-full min-w-0 table-auto border-collapse text-left text-xs">
                    <thead className="sticky top-0 z-[1] border-b border-slate-200 bg-slate-100">
                      <tr className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                        <th scope="col" className="whitespace-nowrap px-2 py-2 text-left align-bottom">
                          SKU
                        </th>
                        <th scope="col" className="min-w-0 px-2 py-2 text-left align-bottom">
                          Error
                        </th>
                        <th scope="col" className="whitespace-nowrap px-2 py-2 text-left align-bottom w-[1%]">
                          Time
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {failModal.errors.map((row, i) => (
                        <tr
                          key={`${row.offerId}-${i}`}
                          className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50/90"
                        >
                          <td className="whitespace-nowrap px-2 py-1.5 align-top font-mono text-[11px] tabular-nums !text-slate-950">
                            {row.offerId != null ? String(row.offerId) : "—"}
                          </td>
                          <td className="min-w-0 max-w-[min(28rem,52vw)] px-2 py-1.5 align-top break-words leading-snug !text-slate-950">
                            {row.error ?? "—"}
                          </td>
                          <td className="whitespace-nowrap px-2 py-1.5 align-top text-[11px] tabular-nums !text-slate-600">
                            {row.timestamp ? new Date(row.timestamp).toLocaleString() : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
